import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSearch, FaBoxOpen } from 'react-icons/fa';
import { FiEdit2, FiTrash2, FiPackage, FiPlus } from 'react-icons/fi';
import AddProductModal from '../../../components/inventory/AddProduct';
import EditProductModal from '../../../components/inventory/EditProduct';
import AddBundleModal from '../../../components/inventory/AddEditBundle';
import { db } from '../protected/firebase';
import { collection, doc, getDocs, deleteDoc, addDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { onSnapshot } from 'firebase/firestore';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';

const Inventory = () => {
  const { user, companyId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [stockLevel, setStockLevel] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Products');
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showEditBundleModal, setShowEditBundleModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(true);
  const [exporting, setExporting] = useState(false);
  const barcodeItems = useMemo(() => {
    if (!products.length) return [];
    const rows = [];

    products.forEach((p) => {
      if (p.barcode) {
        rows.push({
          id: p.id,
          barcode: p.barcode,
          label: p.name || 'Unnamed product',
          sku: p.sku || '',
          variant: false,
        });
      }
      if (p.hasVariants && Array.isArray(p.variants)) {
        p.variants.forEach((v) => {
          if (!v.barcode) return;
          const optionLabel = (v.options || [])
            .map((o) => `${o.group}: ${o.value}`)
            .filter(Boolean)
            .join(' / ');
          rows.push({
            id: `${p.id}-${v.id}`,
            barcode: v.barcode,
            label: optionLabel ? `${p.name} (${optionLabel})` : p.name || 'Variant',
            sku: v.sku || p.sku || '',
            variant: true,
          });
        });
      }
    });

    return rows;
  }, [products]);


  const formatVariantLabel = (variant) => {
    if (!variant) return '';
    if (Array.isArray(variant.options) && variant.options.length > 0) {
      return variant.options
        .map((o) =>
          `${o.optionLabel || o.optKey || o.group || o.name || 'Option'}: ${
            o.valueLabel || o.valueKey || o.value || o.label || 'Value'
          }`
        )
        .join(' / ');
    }
    return variant.name || variant.label || variant.variantName || '';
  };

  const resolveBundleItemLabel = (bundleItem) => {
    const product = products.find((p) => p.id === bundleItem.id);
    const name = product?.name || bundleItem.name;
    if (!product) return { name, variantLabel: bundleItem.variantName || '' };
    if (!bundleItem.variantId || bundleItem.variantId === ANY_VARIANT_ID || bundleItem.allowVariantChoice) {
      return { name, variantLabel: bundleItem.variantName || 'Any variant (choose at checkout)' };
    }
    const variant = product.variants?.find(
      (v) => v.id === bundleItem.variantId || v.variantId === bundleItem.variantId
    );
    const variantLabel = variant ? formatVariantLabel(variant) : bundleItem.variantName || '';
    return { name, variantLabel };
  };

  // Calculate available quantity for a bundle based on its items and current product quantities
  const ANY_VARIANT_ID = '__ANY_VARIANT__';
  const totalVariantQty = (product) =>
    Array.isArray(product?.variants)
      ? product.variants.reduce((sum, v) => sum + Number(v.qty || 0), 0)
      : 0;

  const calculateBundleQuantity = useCallback((bundle, productsList) => {
    if (!bundle.items || bundle.items.length === 0) return 0;

    let minQuantity = Infinity;

    for (const item of bundle.items) {
      const product = productsList.find((p) => p.id === item.id);
      if (!product) return 0; // If any product is missing, bundle is not available

      // Use variant stock if bundle item references a variant; otherwise fallback to product stock
      let availableStock = product.qty ?? totalVariantQty(product);
      if (item.variantId === ANY_VARIANT_ID || item.allowVariantChoice) {
        availableStock = product.qty ?? totalVariantQty(product);
      } else if (item.variantId && product.variants?.length) {
        const variant = product.variants.find((v) => v.id === item.variantId || v.variantId === item.variantId);
        availableStock = variant?.qty ?? 0;
      }

      const available = Math.floor(availableStock / (item.quantity || 1));
      minQuantity = Math.min(minQuantity, available);
    }

    return isFinite(minQuantity) ? minQuantity : 0;
  }, []);

  // Update bundle quantities when products or bundles change
  useEffect(() => {
    if (products.length === 0 || bundles.length === 0 || !companyId) return;

    const updateBundleQuantities = async () => {
      const updates = [];
      
      for (const bundle of bundles) {
        const newQuantity = calculateBundleQuantity(bundle, products);
        
        // Only update if quantity has changed
        if (newQuantity !== bundle.quantity) {
          updates.push(
            updateDoc(
              doc(db, 'companies', companyId, 'inventory', 'data', 'bundles', bundle.id),
              { quantity: newQuantity, updatedAt: new Date().toISOString() }
            )
          );
        }
      }
      
      // Execute all updates in parallel
      await Promise.all(updates);
    };
    
    updateBundleQuantities().catch(console.error);
  }, [products, bundles, companyId, calculateBundleQuantity]);

  // Fetch data
  useEffect(() => {
    if (!companyId) return;
    setLoadingPage(true);
  
    const productRef = collection(db, 'companies', companyId, 'inventory', 'data', 'products');
    const unsubProducts = onSnapshot(productRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(items);
      setLoading(false);
      setLoadingPage(false);
    });
  
    const bundlesRef = collection(db, 'companies', companyId, 'inventory', 'data', 'bundles');
    const unsubBundles = onSnapshot(bundlesRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBundles(items);
      setLoading(false);
      setLoadingPage(false);
    });
  
    return () => {
      unsubProducts();
      unsubBundles();
    };
    
  }, [companyId]);

  const handleEdit = (item) => {
    if (activeTab === 'Products') {
      setSelectedProduct(item);
      setShowEditModal(true);
    } else if (activeTab === 'Bundles') {
      setSelectedBundle(item);
      setShowEditBundleModal(true);
    }
  };

  const handleDelete = async (itemId) => {
    if (!companyId) return;
    
    if (confirm('Are you sure you want to delete this item?')) {
      const path = activeTab === 'Products' ? 'products' : 'bundles';
      await deleteDoc(doc(db, 'companies', companyId, 'inventory', 'data', path, itemId));
      
      if (activeTab === 'Products') {
        setProducts(prev => prev.filter(i => i.id !== itemId));
      } else {
        setBundles(prev => prev.filter(i => i.id !== itemId));
      }
    }
  };

  const handleProductUpdate = (updatedProduct) => {
    setProducts(prev => 
      prev.map(p => p.id === updatedProduct.id ? updatedProduct : p)
    );
  };

  const handleProductAdded = (newProduct) => {
    setProducts(prev => [...prev, newProduct]);
  };

  const exportBarcodesToPdf = useCallback(async () => {
    if (!barcodeItems.length) {
      alert('No barcodes to export yet. Add or generate barcodes first.');
      return;
    }

    setExporting(true);
    try {
      const docPDF = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = docPDF.internal.pageSize.getWidth();
      const pageHeight = docPDF.internal.pageSize.getHeight();
      const margin = 10;
      const columns = 2;
      const gap = 8;
      const labelWidth = (pageWidth - margin * 2 - gap) / columns;
      const labelHeight = 72;
      let y = margin;
      let col = 0;

      barcodeItems.forEach((item) => {
        if (y + labelHeight > pageHeight - margin) {
          docPDF.addPage();
          y = margin;
          col = 0;
        }

        const x = margin + col * (labelWidth + gap);
        docPDF.setFontSize(10);
        docPDF.text(item.label || 'Product', x + 2, y + 6);
        if (item.sku) {
          docPDF.setFontSize(9);
          docPDF.text(`SKU: ${item.sku}`, x + 2, y + 12);
        }
        docPDF.setFontSize(9);
        docPDF.text(item.barcode, x + 2, y + 18);

        const canvas = document.createElement('canvas');
        try {
          JsBarcode(canvas, item.barcode, {
            format: 'CODE128',
            width: 2,
            height: 40,
            displayValue: false,
            margin: 12, // quiet zone for hardware scanners
          });

          const { width: canvasWidth, height: canvasHeight } = canvas;
          const maxImageWidth = Math.min(labelWidth - 4, canvasWidth);
          const targetHeight = (canvasHeight * maxImageWidth) / canvasWidth;
          const imgX = x + (labelWidth - maxImageWidth) / 2;
          const imgY = y + 28;
          const imgData = canvas.toDataURL('image/png');
          docPDF.addImage(imgData, 'PNG', imgX, imgY, maxImageWidth, targetHeight);
        } catch (err) {
          console.error('Barcode rendering failed', err);
          docPDF.setFontSize(9);
          docPDF.text('Invalid barcode', x + 2, y + 24);
        }

        col += 1;
        if (col >= columns) {
          col = 0;
          y += labelHeight;
        }
      });

      docPDF.save('barcodes.pdf');
    } catch (err) {
      console.error('Export barcodes failed', err);
      alert('Failed to export barcodes. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [barcodeItems]);

  const selectedData = activeTab === 'Products' ? products : bundles;
  const typeOptions = activeTab === 'Products'
    ? ['All', ...new Set(products.map(p => p.type))]
    : ['All'];

  const filteredItems = selectedData.filter((item) => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Use quantity for bundles, qty for products
    const itemQty = activeTab === 'Bundles'
      ? calculateBundleQuantity(item, products)
      : item.quantity !== undefined ? item.quantity : item.qty;
    const parsedQty = parseInt(itemQty, 10);
    
    const matchesStock =
      stockLevel === 'All' ||
      (stockLevel === 'In Stock' && parsedQty > 10) ||
      (stockLevel === 'Low Stock' && parsedQty <= 10 && parsedQty > 0) ||
      (stockLevel === 'No Stock' && parsedQty === 0);
      
    const matchesType = typeFilter === 'All' || item.type === typeFilter;
    return matchesSearch && matchesStock && matchesType;
  });

// Sort by SKU naturally (handles letters + numbers like NF1, AK2)
const sortedItems = [...filteredItems].sort((a, b) => {
  const skuA = (a.sku || '').toUpperCase();
  const skuB = (b.sku || '').toUpperCase();
  
  // Natural sort: split into chunks of numbers & letters
  const partsA = skuA.match(/\d+|\D+/g) || [];
  const partsB = skuB.match(/\d+|\D+/g) || [];
  
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i] || '';
    const partB = partsB[i] || '';
    
    // Compare numbers as numbers, letters as strings
    const numA = parseInt(partA, 10);
    const numB = parseInt(partB, 10);
    
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) return numA - numB;
    } else {
      const cmp = partA.localeCompare(partB);
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
});

const totalPages = Math.max(1, Math.ceil(sortedItems.length / itemsPerPage));
const paginatedItems = sortedItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  if (loadingPage) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading inventory...</span>
      </div>
    );
  }
  return (
    <div className="text-white px-4 py-6 md:px-6 lg:px-12 max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Inventory</h1>
          <p className="text-sm text-gray-400">Products you are selling and bundles you offer.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            className="bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded whitespace-nowrap disabled:opacity-50"
            onClick={exportBarcodesToPdf}
            disabled={exporting || !barcodeItems.length}
          >
            {exporting ? 'Exporting…' : 'Export Barcodes'}
          </button>
          <button
            className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded whitespace-nowrap"
            onClick={() => {
              setSelectedProduct(null);
              if (activeTab === 'Bundles') {
                setShowBundleModal(true);
              } else {
                setShowAddModal(true);
              }
            }}
          >
            + Add {activeTab === 'Products' ? 'Product' : 'Bundle'}
          </button>
        </div>
      </div>

      <div className="flex space-x-2 mb-6 border-b border-gray-700 overflow-x-auto">
        {['Products', 'Bundles'].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setCurrentPage(1);
              setSearchTerm('');
              setStockLevel('All');
              setTypeFilter('All');
            }}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === tab
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap md:items-center gap-4 mb-6">
        <div className="flex flex-col text-sm flex-1 min-w-[200px]">
          <label className="mb-1 text-gray-400">
            {activeTab === 'Products' ? 'Search Product' : 'Search Bundle'}
          </label>
          <input
            type="text"
            placeholder="Search by name or SKU"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white w-full"
          />
        </div>

        <div className="flex flex-col text-sm flex-1 min-w-[150px]">
          <label className="mb-1 text-gray-400">Stock Level</label>
          <select
            value={stockLevel}
            onChange={(e) => setStockLevel(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
          >
            <option value="All">All Stock</option>
            <option value="In Stock">In Stock</option>
            <option value="Low Stock">Low Stock</option>
            <option value="No Stock">No Stock</option>
          </select>
        </div>

        <div className="flex flex-col text-sm flex-1 min-w-[150px]">
          <label className="mb-1 text-gray-400">
            {activeTab === 'Products' ? 'Product Type' : 'Type'}
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
          >
            {typeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="bg-gray-900 rounded-lg p-8 text-center">
          <FaBoxOpen className="mx-auto text-5xl text-gray-600 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No items found</h3>
          <p className="text-gray-400 mb-4">
            {searchTerm || stockLevel !== 'All' || typeFilter !== 'All' 
              ? 'Try adjusting your search or filter criteria.'
              : `You don't have any ${activeTab.toLowerCase()} in your inventory yet.`}
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setStockLevel('All');
              setTypeFilter('All');
              activeTab === 'Products' ? setShowAddModal(true) : setShowBundleModal(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded"
          >
            Add {activeTab === 'Products' ? 'Product' : 'Bundle'}
          </button>
        </div>
      ) : (
        <>
          {activeTab === 'Bundles' ? (
            <div className="bg-gray-900/50 rounded-lg p-6">
              {bundles.length === 0 ? (
                <div className="text-center py-12 bg-gray-900/50 rounded-lg border-2 border-dashed border-gray-700">
                  <FiPackage className="mx-auto w-12 h-12 text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium text-white">No bundles found</h3>
                  <p className="text-gray-400 mt-1 mb-4">Create your first bundle to get started</p>
                  <button
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    onClick={() => setShowBundleModal(true)}
                  >
                    <FiPlus className="w-4 h-4" />
                    Create Bundle
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedItems.map((bundle) => {
                    const availableSets = calculateBundleQuantity(bundle, products);
                    return (
                    <div key={bundle.id} className="bg-gray-800/50 p-4 rounded-xl border border-gray-700/50 hover:border-purple-500/30 transition-colors shadow-lg">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <h3 className="font-semibold text-white text-lg">{bundle.name}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-sm bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded">
                              SKU: {bundle.sku || 'N/A'}
                            </span>
                            <span className="text-sm bg-green-900/30 text-green-400 px-2 py-0.5 rounded">
                              RM {parseFloat(bundle.price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(bundle)}
                            className="p-2.5 text-purple-400 bg-purple-900/40 rounded-lg hover:bg-purple-800/60 transition-all hover:scale-110 active:scale-95"
                            title="Edit Bundle"
                          >
                            <FiEdit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(bundle.id)}
                            className="p-2.5 text-red-400 bg-red-900/40 rounded-lg hover:bg-red-800/60 transition-all hover:scale-110 active:scale-95"
                            title="Delete Bundle"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3 border-t border-gray-700/50">
                        <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                          <span>Includes ({bundle.items?.length || 0} items)</span>
                        </h4>
                        <ul className="space-y-2">
                          {bundle.items?.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-800/30 px-3 py-1.5 rounded-lg">
                              <div className="text-sm text-gray-300 truncate max-w-[70%]">
                                <div className="truncate">{item.name}</div>
                                {item.variantName && (
                                  <div className="text-[11px] text-gray-400 truncate">{item.variantName}</div>
                                )}
                              </div>
                              <span className="text-xs bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded">
                                {item.quantity}x
                              </span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="mt-3 pt-2 text-xs text-gray-400 border-t border-gray-700/30">
                          <div className="flex justify-between items-center">
                            <span>Available:</span>
                            <span className="font-medium text-white">
                              {availableSets} {availableSets === 1 ? 'set' : 'sets'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
);})}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#1a1a1a] rounded-lg border border-gray-700 p-0 m-0 overflow-hidden">
              <div className="w-full overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <table className="min-w-full w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 bg-gray-800">
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Quantity</th>
                    <th className="px-4 py-3">Sale Price (RM)</th>
                    <th className="px-4 py-3">Purchase Price (RM)</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedItems.map((item) => {
                    const totalQty = item.qty ?? 0;
                    const badgeClass = totalQty <= 0 ? 'bg-red-900/30 text-red-400' : totalQty <= 10 ? 'bg-yellow-900/30 text-yellow-400' : 'bg-green-900/30 text-green-400';
                    return (
                      <tr key={item.id} className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-gray-300">{item.sku || '-'}</td>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                              {totalQty}
                            </span>
                            {item.hasVariants && item.variants?.length > 0 && (
                              <div className="text-[11px] text-gray-400 space-y-0.5">
                                {item.variants.map((v, idx) => (
                                  <div key={`${item.id}-v-${idx}`} className="flex justify-between gap-2">
                                    <span className="truncate">{formatVariantLabel(v) || '-'}</span>
                                    <span className="text-gray-300">{v.qty ?? 0}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-300">RM {parseFloat(item.price || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-400">{item.purchase ? `RM ${parseFloat(item.purchase).toFixed(2)}` : '-'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-900/30 text-purple-400">
                            {item.type || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-purple-400 bg-purple-900/30 rounded-lg hover:bg-purple-800/50 transition-colors hover:scale-105 active:scale-95"
                              title="Edit"
                            >
                              <FiEdit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-2 text-red-400 bg-red-900/30 rounded-lg hover:bg-red-800/50 transition-colors hover:scale-105 active:scale-95"
                              title="Delete"
                            >
                              <FiTrash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {paginatedItems.length === 0 && (
                    <tr>
                      <td className="px-4 py-8 text-center text-gray-400" colSpan={7}>
                        <div className="flex flex-col items-center justify-center">
                          <FiPackage className="w-12 h-12 text-gray-600 mb-2" />
                          <p className="text-gray-400">No {activeTab.toLowerCase()} found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center flex-wrap mt-4 gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded border border-gray-700 bg-gray-800 text-white disabled:opacity-50"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded ${
                currentPage === page
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded border border-gray-700 bg-gray-800 text-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showAddModal && activeTab === 'Products' && (
        <AddProductModal 
          isOpen={showAddModal} 
          onClose={() => setShowAddModal(false)} 
          onProductAdded={handleProductAdded}
          companyId={companyId}
        />
      )}

      {showEditModal && selectedProduct && (
        <EditProductModal 
          isOpen={showEditModal} 
          onClose={() => setShowEditModal(false)} 
          product={selectedProduct}
          onUpdate={handleProductUpdate}
          companyId={companyId}
        />
      )}

      {showBundleModal && (
        <AddBundleModal 
          isOpen={showBundleModal} 
          onClose={() => setShowBundleModal(false)} 
          products={products}
          companyId={companyId}
        />
      )}

      {showEditBundleModal && selectedBundle && (
        <AddBundleModal 
          isOpen={showEditBundleModal} 
          onClose={() => setShowEditBundleModal(false)} 
          bundle={selectedBundle}
          products={products}
          companyId={companyId}
        />
      )}
    </div>
  );
};

export default Inventory;
